import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  Injectable,
  Injector,
  Type,
  inject,
} from '@angular/core';
import { DialogService as PrimeDialogService } from 'primeng/dynamicdialog';
import { Observable, map } from 'rxjs';
import { AlertDialogContentComponent } from './alert-dialog-content.component';
import { ConfirmDialogContentComponent } from './confirm-dialog-content.component';
import { DialogFrameComponent } from './dialog-frame.component';
import {
  AlertOptions,
  ConfirmOptions,
  DialogContent,
  DialogDataOf,
  DialogFrameData,
  DialogOpenOptions,
  DialogRef,
  DialogResult,
  DialogResultOf,
  DialogWidth,
} from './dialog.models';

const WIDTHS: Record<string, string> = {
  sm: '420px',
  md: '640px',
  lg: '900px',
};

/**
 * The one way pages open dialogs and ask for confirmation. Pages never
 * import PrimeNG for this: PrimeNG's DynamicDialog sits behind this service
 * (and only `libs/shell` knows about it), so the component library can be
 * swapped without touching a page.
 *
 * Content components implement `DialogContent<D, R>` (data in through
 * `DIALOG_DATA`, result out through `DialogRef.ok(data)` or `cancel()`) and
 * put their buttons in `<sh-dialog-footer>`. `open` infers `D` and `R`:
 *
 * ```ts
 * class RenameDialog implements DialogContent<{ name: string }, string> {
 *   readonly data = inject<{ name: string }>(DIALOG_DATA);
 *   readonly dialogRef = inject<DialogRef<string>>(DialogRef);
 *   save(name: string) { this.dialogRef.ok(name); }
 * }
 *
 * dialogs.open(RenameDialog, { title: 'Rename', data: { name: 'A' } })
 *   .afterOk.subscribe((name) => ...); // name: string
 * // afterClosed gives { action: 'ok' | 'cancel' | 'dismiss'; data?: string }
 * ```
 *
 * The frame (title, close button, body) is our own; PrimeNG's dialog is
 * opened with `showHeader: false`. Styling: `@seahorse/theme`, `_dialog.scss`.
 *
 * Note: content components are created from the root injector, so they can
 * only inject root-provided services (plus `DIALOG_DATA` and `DialogRef`),
 * not providers of a lazy-loaded module.
 *
 * Stuck mask: the old `<p-confirmDialog>` needed `[appendTo]="null"`, because
 * with the default (`body`) the p-dialog container is re-parented to the body
 * and its close animation never reported done, leaving the full-screen mask.
 * DynamicDialog does not re-parent anything: its host element is attached
 * to the body once, by `createComponent`, and the mask and container stay
 * inside it, so the close animation's `done` callback destroys the whole
 * component (mask included). No workaround is needed (by code reading; see
 * the step 11.1 report for the browser check).
 */
@Injectable({ providedIn: 'root' })
export class DialogService {
  // PrimeNG's DialogService is not `providedIn: 'root'`; the shell owns it
  // so pages (and apps) don't have to provide anything.
  private readonly _prime = new PrimeDialogService(
    inject(ApplicationRef),
    inject(Injector),
    inject(DOCUMENT)
  );

  /**
   * Opens `component` in a dialog. The data and result types are inferred
   * from the component's `DialogContent<D, R>`.
   */
  open<C extends DialogContent<any, any>>(
    component: Type<C>,
    options: DialogOpenOptions<DialogDataOf<C>>
  ): DialogRef<DialogResultOf<C>> {
    const ref = new DialogRef<DialogResultOf<C>>();
    const closable = options.closable ?? true;

    const frame: DialogFrameData = {
      component,
      title: options.title,
      data: options.data,
      closable,
      ref,
    };

    const primeRef = this._prime.open(DialogFrameComponent, {
      data: frame,
      width: this.widthOf(options.width),
      showHeader: false,
      modal: true,
      closable,
      closeOnEscape: closable,
      dismissableMask: options.dismissableMask ?? false,
      // Every dialog uses the same frame component; without this PrimeNG
      // refuses to open a second dialog while one is open.
      duplicate: true,
      styleClass: 'sh-dialog',
      maskStyleClass: 'sh-dialog-mask',
    });

    ref._attach((result) => primeRef.close(result));
    // PrimeNG closes with no value for the X button, Esc and the mask.
    primeRef.onClose.subscribe((result?: DialogResult<DialogResultOf<C>>) =>
      ref._complete(result ?? { action: 'dismiss' })
    );
    // Safety net: a dialog destroyed without `close` still completes.
    primeRef.onDestroy.subscribe(() => ref._complete({ action: 'dismiss' }));

    return ref;
  }

  /** Emits `true` on confirm; `false` on cancel, X or Esc. Then completes. */
  confirm(options: ConfirmOptions): Observable<boolean> {
    return this.open(ConfirmDialogContentComponent, {
      title: options.title,
      data: options,
      width: 'sm',
    }).afterClosed.pipe(map((result) => result.action === 'ok'));
  }

  /**
   * A message with a single OK button (the plain "alert"): title and text,
   * no choice. Emits once, when the dialog closes (OK, X or Esc), then
   * completes; nothing is emitted when it never closes. With `showOk: false`
   * and `closable: false` it has no way out, for a notice the app acts on
   * itself.
   *
   * ```ts
   * dialogs.alert({ title: 'Saved', message: 'Your changes are saved.' })
   *   .subscribe(() => this.goBack());
   * ```
   */
  alert(options: AlertOptions): Observable<void> {
    return this.open(AlertDialogContentComponent, {
      title: options.title,
      data: options,
      width: 'sm',
      closable: options.closable ?? true,
    }).afterClosed.pipe(map(() => undefined));
  }

  private widthOf(width: DialogWidth | undefined): string {
    return WIDTHS[width ?? 'md'] ?? (width as string);
  }
}
